/* eslint-disable filenames/match-regex, import/no-unused-modules */
import classNames from 'classnames';
import _ from 'lodash';
import { parse, stringify } from 'query-string';
import React, { useEffect, useMemo } from 'react';
import type { NavigationType, To as V6To } from 'react-router-dom';
import {
  NavLink,
  // eslint-disable-next-line no-restricted-imports
  useNavigate,
  useNavigationType,
  useParams,
  useLocation as useV6Location,
} from 'react-router-dom';

type V3DefaultQuery<T = string> = {
  [key: string]: T | T[] | null | undefined;
};

interface V3Location<Q = V3DefaultQuery> {
  pathname: string;
  search: string;
  query: Q;
  hash: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any;
  key: string;
  action: 'POP' | 'PUSH' | 'REPLACE';
}

type V3DefaultQueryLike = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

interface V3LocationDescriptorObject {
  pathname?: string;
  search?: string;
  query?: V3DefaultQueryLike;
  hash?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state?: any;
}

type V3LocationDescriptor = V3LocationDescriptorObject | string;
type V3ToLocationFunction = (location: V3Location) => V3LocationDescriptor;
export type V3To = V3LocationDescriptor | V3ToLocationFunction;

type V6Location = ReturnType<typeof useV6Location>;

const rrV6LocationToV3 = (
  location: V6Location,
  action: NavigationType,
): V3Location => ({
  ...location,
  query: parse(location.search.substring(1)),
  action,
});

/**
 * @deprecated use useLocation from `react-router-dom` directly
 */
export const useLocation = (): V3Location => {
  const location = useV6Location();
  const action = useNavigationType();
  const newLocation = useMemo(
    () => rrV6LocationToV3(location, action),
    [action, location],
  );

  return newLocation;
};

export const rrV3V6ToConverter = (
  to?: V3LocationDescriptor,
): [V6To, unknown?] => {
  if (!to) {
    return [{}, undefined];
  }

  if (typeof to === 'string') {
    return [to, undefined];
  }

  const { query, search, state, ...rest } = to;
  const querySearchString =
    query && `?${stringify(query).replace(/%20/g, '+')}`;
  return [{ search: querySearchString || search, ...rest }, state];
};

const getNavigationFunctions = (
  navigate: ReturnType<typeof useNavigate>,
  location: V3Location,
) => {
  const getFunc = (replace?: boolean) => (to?: V3To) => {
    const simpleTo = typeof to === 'function' ? to(location) : to;
    const [v6To, state] = rrV3V6ToConverter(simpleTo);
    if (!v6To) return;

    navigate(v6To, { state, replace });
  };

  return {
    push: getFunc(),
    replace: getFunc(true),
  };
};

/**
 * @deprecated use hooks from `react-router-dom` directly
 */
export const useRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const navigationFunctions = useMemo(
    () => getNavigationFunctions(navigate, location),
    [navigate, location],
  );

  const routerRef = React.useRef({
    location,
    params,
    ...navigationFunctions,
  });

  useEffect(() => {
    routerRef.current.location = location;
  }, [location]);

  useEffect(() => {
    routerRef.current.params = params;
  }, [params]);

  useEffect(() => {
    routerRef.current.push = navigationFunctions.push;
    routerRef.current.replace = navigationFunctions.replace;
  }, [navigationFunctions]);

  return routerRef.current;
};

/**
 * @deprecated use `react-router-dom` hooks directly
 */
export const withRouter = <P,>(Component: React.FunctionComponent<P>) => {
  const ComponentWithRouterProp = (
    props: Omit<P, 'router' | 'params' | 'location'>,
  ) => {
    const router = useRouter();
    const location = useLocation();
    const params = useParams();

    return (
      <Component
        {...(props as P)}
        router={router}
        params={params}
        location={location}
      />
    );
  };

  return ComponentWithRouterProp;
};

type SearchLocation = { search?: string } | string;

const getSearch = (location: SearchLocation): string => {
  if (typeof location === 'string') {
    return new URL(location, window.location.origin).search || '';
  }

  return location.search || '';
};

const isSearchEqual = (
  loc1: SearchLocation = '',
  loc2: SearchLocation = '',
): boolean => {
  const search1 = getSearch(loc1);
  const search2 = getSearch(loc2);

  if (search1 === search2) return true;

  const toSortedArray = (search: string) =>
    search.substring(1).split('&').toSorted();

  return _.isEqual(toSortedArray(search1), toSortedArray(search2));
};

interface RRV3LinkProps
  extends Omit<React.ComponentProps<typeof NavLink>, 'to' | 'end'> {
  className?: string;
  activeClassName?: string;
  onlyActiveOnIndex?: boolean;
  onlyActiveOnSearchMatch?: boolean;
  to: V3To;
}

/**
 * @deprecated Use `Link` or `NavLink` from `react-router-dom` instead.
 */
export const Link = ({
  className,
  activeClassName,
  onlyActiveOnIndex,
  onlyActiveOnSearchMatch,
  to,
  // Matching previous behavior
  style = {},
  ...props
}: RRV3LinkProps) => {
  const v3Location = useLocation();
  const v3LocationDescriptor = typeof to === 'function' ? to(v3Location) : to;
  const [v6To, state] = rrV3V6ToConverter(v3LocationDescriptor);

  return (
    <NavLink
      to={v6To}
      state={state}
      className={(navState) => {
        // v6 isActive only look at pathname even with `end` prop
        const isActive =
          navState.isActive &&
          (!onlyActiveOnSearchMatch || isSearchEqual(v3Location, v6To));

        return (
          classNames(
            className,
            activeClassName && { [activeClassName]: isActive },
          ) || undefined
        );
      }}
      end={onlyActiveOnIndex}
      style={style}
      {...props}
    />
  );
};
